import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const ClientGalleryWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e1d1d 0%, #2c2c2c 100%);
  color: #ffffff;
  padding: 8rem 2rem 6rem 2rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  position: fixed;
  top: 2rem;
  left: 2rem;
  padding: 1rem 2.5rem;
  background: rgba(255, 114, 0, 0.25);
  border: 2px solid rgba(255, 114, 0, 0.6);
  border-radius: 2rem;
  color: #ff7200;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);

  &:hover {
    background: rgba(255, 114, 0, 0.35);
    transform: translateX(-5px);
    box-shadow: 0 6px 20px rgba(255, 114, 0, 0.3);
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    
    ${props => props.isScrolled && `
      background: rgba(255, 114, 0, 0.35);
      border-color: rgba(255, 114, 0, 0.8);
    `}

    &:hover {
      transform: ${props => props.isScrolled ? 'translateY(-3px)' : 'translateX(-5px)'};
    }
  }
`;

const GalleryHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #ff7200;
  }

  .category-name {
    font-size: 1rem;
    color: #dec8a5;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageCard = styled.div`
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  aspect-ratio: 4/3;
  cursor: pointer;
  transition: all 0.5s ease;
  opacity: ${props => props.visible ? 1 : 0};
  transform: scale(${props => props.visible ? 1 : 0.9}) translateY(${props => props.visible ? 0 : '20px'});
  transition-delay: ${props => props.delay}ms;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);

  &:hover {
    transform: scale(1.05) translateY(-10px);
    box-shadow: 0 20px 50px rgba(255, 114, 0, 0.3);
  }

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: 0.5s ease;
  }

  &:hover img {
    transform: scale(1.1);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, transparent 100%);
  opacity: 0;
  transition: 0.3s ease;

  ${ImageCard}:hover & {
    opacity: 1;
  }

  h3 {
    color: #ffffff;
    margin-bottom: 0.3rem;
    font-size: 1.2rem;
  }

  p {
    color: #dec8a5;
    font-size: 0.9rem;
    margin: 0;
  }
`;

const PlayIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 4rem;
  height: 4rem;
  background: rgba(255, 114, 0, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s ease;
  z-index: 2;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 1.2rem solid white;
    border-top: 0.8rem solid transparent;
    border-bottom: 0.8rem solid transparent;
    margin-left: 0.3rem;
  }

  ${ImageCard}:hover & {
    background: rgba(255, 114, 0, 1);
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 0.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
`;

const ModalVideo = styled.video`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 0.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
`;

const ModalInfo = styled.div`
  text-align: center;
  color: #ffffff;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: #ff7200;
  }

  p {
    color: #dec8a5;
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  background: rgba(255, 114, 0, 0.3);
  border: 2px solid rgba(255, 114, 0, 0.6);
  border-radius: 50%;
  color: #ff7200;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s ease;
  z-index: 10001;

  &:hover {
    background: rgba(255, 114, 0, 0.5);
    transform: rotate(90deg);
  }

  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1.2rem;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
  transform: translateY(-50%);
  width: 3rem;
  height: 3rem;
  background: rgba(255, 114, 0, 0.3);
  border: 2px solid rgba(255, 114, 0, 0.6);
  border-radius: 50%;
  color: #ff7200;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s ease;
  z-index: 10001;

  &:hover {
    background: rgba(255, 114, 0, 0.5);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1.2rem;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 114, 0, 0.3);
  border: 2px solid rgba(255, 114, 0, 0.6);
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  color: #ff7200;
  font-weight: 700;
  font-size: 1rem;
`;

const ClientGallery = () => {
  const { category, client } = useParams();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedImage]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mapeamento de slugs de clientes para nomes exibidos
  const clientNameMap = {
    'bee-happy': 'Bee Happy',
    'center-marc': 'Center Marc',
    'l3-engenharia-ambiental': 'L3 Engenharia Ambiental',
    'lirios': 'Lirios',
    'movimento-eco': 'Movimento Eco',
    'pet-life': 'Pet Life',
    'red-nose': 'Red Nose',
    'sancla': 'Sanclá',
    'pericles': 'Pericles',
    'arte-esportiva': 'Arte Esportiva'
  };

  // Dados das imagens por cliente e categoria
  const galleryData = {
    'gestao-midias': {
      'bee-happy': [
        { url: '/assets/images/portfólio qc/bee happy/artes/49e91e218130827.679bf03a366bb.webp', title: 'Arte Bee Happy', description: 'Gestão de Mídias' }
      ],
      'center-marc': [
        { url: '/assets/images/portfólio qc/Center Marc/artes/IMG_4161.JPG', title: 'Arte Center Marc', description: 'Gestão de Mídias' }
      ],
      'l3-engenharia-ambiental': [
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4177.JPG', title: 'Arte L3 - 1', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4178.JPG', title: 'Arte L3 - 2', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4179.JPG', title: 'Arte L3 - 3', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4180.JPG', title: 'Arte L3 - 4', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4181.JPG', title: 'Arte L3 - 5', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4182.JPG', title: 'Arte L3 - 6', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4183.JPG', title: 'Arte L3 - 7', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4184.JPG', title: 'Arte L3 - 8', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4185.JPG', title: 'Arte L3 - 9', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/artes/IMG_4187.JPG', title: 'Arte L3 - 10', description: 'Gestão de Mídias' }
      ],
      'lirios': [
        { url: '/assets/images/portfólio qc/lirios/artes/IMG_4149.JPG', title: 'Arte Lirios - 1', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/lirios/artes/IMG_4150.JPG', title: 'Arte Lirios - 2', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/lirios/artes/IMG_4151.JPG', title: 'Arte Lirios - 3', description: 'Gestão de Mídias' }
      ],
      'movimento-eco': [
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4188.JPG', title: 'Arte Movimento Eco - 1', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4189.JPG', title: 'Arte Movimento Eco - 2', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4190.JPG', title: 'Arte Movimento Eco - 3', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4191.JPG', title: 'Arte Movimento Eco - 4', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4192.JPG', title: 'Arte Movimento Eco - 5', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4193.JPG', title: 'Arte Movimento Eco - 6', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4194.JPG', title: 'Arte Movimento Eco - 7', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4195.JPG', title: 'Arte Movimento Eco - 8', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4196.JPG', title: 'Arte Movimento Eco - 9', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Movimento eco/artes/IMG_4197.JPG', title: 'Arte Movimento Eco - 10', description: 'Gestão de Mídias' }
      ],
      'pet-life': [
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4167.JPG', title: 'Arte Pet Life - 1', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4168.JPG', title: 'Arte Pet Life - 2', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4169.JPG', title: 'Arte Pet Life - 3', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4170.JPG', title: 'Arte Pet Life - 4', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4171.JPG', title: 'Arte Pet Life - 5', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4172.JPG', title: 'Arte Pet Life - 6', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4173.JPG', title: 'Arte Pet Life - 7', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4174.JPG', title: 'Arte Pet Life - 8', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4175.JPG', title: 'Arte Pet Life - 9', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pet life/artes/IMG_4176.JPG', title: 'Arte Pet Life - 10', description: 'Gestão de Mídias' }
      ],
      'red-nose': [
        { url: '/assets/images/portfólio qc/red nose/artes/Carrossel Redes Sociais.jpg', title: 'Carrossel Redes Sociais', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/red nose/artes/Padrão Redes Sociais.jpg', title: 'Padrão Redes Sociais', description: 'Gestão de Mídias' }
      ],
      'sancla': [
        { url: '/assets/images/portfólio qc/Sancla/artes/09c286225007803.6815497c2abd3.png', title: 'Arte Sanclá - 1', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/2f9044225007803.6815497c28b21.png', title: 'Arte Sanclá - 2', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/30f117225007803.6815497c28656.png', title: 'Arte Sanclá - 3', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/3d5571225007803.6815497c2bb6c.png', title: 'Arte Sanclá - 4', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/589f2e225007803.6815497c29fbf.webp', title: 'Arte Sanclá - 5', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/85b8f7225007803.6815497c2a48a.webp', title: 'Arte Sanclá - 6', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/8e40b8225007803.6815497c2c226.png', title: 'Arte Sanclá - 7', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/b2891f225007803.6815497c299ce.png', title: 'Arte Sanclá - 8', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/d8377c225007803.6815497c290ad.png', title: 'Arte Sanclá - 9', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Sancla/artes/fe7910225007803.6815497c2b3ff.webp', title: 'Arte Sanclá - 10', description: 'Gestão de Mídias' }
      ],
      'pericles': [
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4152.JPG', title: 'Arte Pericles - 1', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4153.JPG', title: 'Arte Pericles - 2', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4154.JPG', title: 'Arte Pericles - 3', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4155.JPG', title: 'Arte Pericles - 4', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4156.JPG', title: 'Arte Pericles - 5', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4157.JPG', title: 'Arte Pericles - 6', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4158.JPG', title: 'Arte Pericles - 7', description: 'Gestão de Mídias' },
        { url: '/assets/images/portfólio qc/Pericles/artes/IMG_4159.JPG', title: 'Arte Pericles - 8', description: 'Gestão de Mídias' }
      ],
      'arte-esportiva': [
        { url: '/assets/images/portfólio qc/Arte esportva/artes/028-SUECIA.png', title: 'Suécia', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/041-WEEKND.png', title: 'The Weeknd', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/059-NETHERLANDS.png', title: 'Holanda', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/074-KansasCity.png', title: 'Kansas City', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/102-Young.png', title: 'Young', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/105-Magic.png', title: 'Magic', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/132-ADRIANO.png', title: 'Adriano', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/143-KAKÁ x MILAN.png', title: 'Kaká x Milan', description: 'Arte Esportiva' },
        { url: '/assets/images/portfólio qc/Arte esportva/artes/149-RONALDO-FENÔMENO.png', title: 'Ronaldo Fenômeno', description: 'Arte Esportiva' }
      ]
    },
    'comunicacao-integrada': {
      'bee-happy': [
        { url: '/assets/images/portfólio qc/bee happy/comunicacao integrada/184c47218130827.679bf03a36e5e.webp', title: 'Comunicação Bee Happy - 1', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/bee happy/comunicacao integrada/3d9bd8218130827.679bf03a360d0.webp', title: 'Comunicação Bee Happy - 2', description: 'Comunicação Integrada' }
      ],
      'red-nose': [
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/adesivos.jpg', title: 'Adesivos', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/CAMISA PRETA.png', title: 'Camisa Preta', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/CAMISA VERDE.png', title: 'Camisa Verde', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/Circuito  Metro de Sao Paulo.jpg', title: 'Circuito Metrô de São Paulo', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/Circuito Relogio Digital _ Sao Paulo.jpg', title: 'Circuito Relógio Digital', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/Institucional Red Nose (comercial) - Metro SP.jpg', title: 'Institucional Metrô SP', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/Outdoor Red nose.png', title: 'Outdoor', description: 'Comunicação Integrada' },
        { url: '/assets/images/portfólio qc/red nose/comunicacao integrada/Ponto de ônibus.jpg', title: 'Ponto de Ônibus', description: 'Comunicação Integrada' }
      ]
    },
    'videos': {
      'bee-happy': [
        { url: '/assets/images/portfólio qc/bee happy/vídeos/bee-happy-video.mp4', title: 'Vídeo Bee Happy', description: 'Produção Audiovisual', isVideo: true }
      ],
      'l3-engenharia-ambiental': [
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/vídeos/Cópia de L3 Engenharia Ambiental 07.mp4', title: 'L3 Engenharia - Vídeo 1', description: 'Produção Audiovisual', isVideo: true },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/vídeos/Cópia de L3 Engenharia Ambiental 08.mp4', title: 'L3 Engenharia - Vídeo 2', description: 'Produção Audiovisual', isVideo: true },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/vídeos/Cópia de L3 Engenharia Ambiental 11.mp4', title: 'L3 Engenharia - Vídeo 3', description: 'Produção Audiovisual', isVideo: true },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/vídeos/Cópia de L3 Engenharia Ambiental 12.mp4', title: 'L3 Engenharia - Vídeo 4', description: 'Produção Audiovisual', isVideo: true },
        { url: '/assets/images/portfólio qc/L3 engenharia ambiental/vídeos/Cópia de L3 Engenharia Ambiental 13.mp4', title: 'L3 Engenharia - Vídeo 5', description: 'Produção Audiovisual', isVideo: true }
      ],
      'movimento-eco': [
        { url: '/assets/images/portfólio qc/Movimento eco/vídeos/Cópia de Movimento Eco 02.mp4', title: 'Movimento Eco - Vídeo 1', description: 'Produção Audiovisual', isVideo: true },
        { url: '/assets/images/portfólio qc/Movimento eco/vídeos/Cópia de movimento eco.mp4', title: 'Movimento Eco - Vídeo 2', description: 'Produção Audiovisual', isVideo: true }
      ],
      'pericles': [
        { url: '/assets/images/portfólio qc/Pericles/vídeos/pericles-video.mp4', title: 'Vídeo Pericles', description: 'Produção Audiovisual', isVideo: true }
      ]
    }
  };

  const categoryNames = {
    'gestao-midias': 'Gestão de Mídias',
    'comunicacao-integrada': 'Comunicação Integrada',
    videos: 'Vídeos'
  };

  const clientImages = galleryData[category]?.[client];
  const clientName = clientNameMap[client];

  if (!clientImages || !clientName) {
    return (
      <ClientGalleryWrapper>
        <Container>
          <BackButton onClick={() => navigate(`/portfolio/${category}`)}>
            ← Voltar
          </BackButton>
          <GalleryHeader>
            <h1>Cliente não encontrado</h1>
            <p>Categoria: {category}, Cliente: {client}</p>
          </GalleryHeader>
        </Container>
      </ClientGalleryWrapper>
    );
  }

  const clientData = {
    name: clientName,
    images: clientImages
  };

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handlePrevImage = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setSelectedImage(clientData.images[newIndex]);
    }
  };

  const handleNextImage = () => {
    if (currentIndex < clientData.images.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setSelectedImage(clientData.images[newIndex]);
    }
  };

  const handleBackToCategory = () => {
    const isMobile = window.innerWidth <= 768;
    
    // No mobile, verifica se está rolado
    if (isMobile && window.scrollY > 100) {
      // Se estiver rolada no mobile, volta para o topo
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Se estiver no topo (mobile) ou em desktop, navega para a página anterior
      navigate(`/portfolio/${category}`);
    }
  };

  return (
    <ClientGalleryWrapper>
      <Container>
        <BackButton onClick={handleBackToCategory} isScrolled={isScrolled}>
          {isScrolled ? '↑ Voltar ao Topo' : `← Voltar para ${categoryNames[category]}`}
        </BackButton>
        <GalleryHeader>
          <div className="category-name">{categoryNames[category]}</div>
          <h1>{clientData.name}</h1>
          <p>Portfólio completo de projetos realizados</p>
        </GalleryHeader>
        <ImageGrid>
          {clientData.images.map((image, index) => (
            <ImageCard
              key={index}
              visible={visible}
              delay={index * 100}
              onClick={() => handleImageClick(image, index)}
            >
              {image.isVideo ? (
                <>
                  <video 
                    src={image.url} 
                    style={{ pointerEvents: 'none' }}
                  />
                  <PlayIcon />
                </>
              ) : (
                <img src={image.url} alt={image.title} />
              )}
              <ImageOverlay>
                <h3>{image.title}</h3>
                <p>{image.description}</p>
              </ImageOverlay>
            </ImageCard>
          ))}
        </ImageGrid>
      </Container>

      {/* Modal de visualização em tela cheia */}
      <ModalOverlay isOpen={selectedImage !== null} onClick={handleCloseModal}>
        <CloseButton onClick={handleCloseModal}>×</CloseButton>
        
        {selectedImage && (
          <>
            <NavigationButton 
              direction="left" 
              onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
              disabled={currentIndex === 0}
            >
              ‹
            </NavigationButton>

            <ModalContent onClick={(e) => e.stopPropagation()}>
              {selectedImage.isVideo ? (
                <ModalVideo src={selectedImage.url} controls />
              ) : (
                <ModalImage src={selectedImage.url} alt={selectedImage.title} />
              )}
              <ModalInfo>
                <h3>{selectedImage.title}</h3>
                <p>{selectedImage.description}</p>
              </ModalInfo>
            </ModalContent>

            <NavigationButton 
              direction="right" 
              onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
              disabled={currentIndex === clientData.images.length - 1}
            >
              ›
            </NavigationButton>

            <ImageCounter>
              {currentIndex + 1} / {clientData.images.length}
            </ImageCounter>
          </>
        )}
      </ModalOverlay>
    </ClientGalleryWrapper>
  );
};

export default ClientGallery;
